import { ArrowBackIcon } from "@chakra-ui/icons";
import {
  Box,
  FormControl,
  Heading,
  Input,
  IconButton,
  Button,
  useDisclosure,
  VStack,
  HStack,
  Text,
  SimpleGrid,
  Container,
  FormLabel,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  InputGroup,
  InputRightElement,
  useToast,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import EmployeeSuccess from "../../../components/modal/EmployeeSuccess";
import {
  useFetchDepartments,
  useFetchLocations,
  useFetchReportTo,
  useFetchShift,
  useAddLocation,
  useAddDepartment,
  useAddReportTo,
  useAddShift,
} from "../../../services/configurationService";
import Select from "react-select";
import { useImageUpload } from "../../../components/imgUpload";
import {
  useAddEmployees,
  useEditEmployee,
  useGetEmployees,
} from "../../../services/employeeService";
import { usePlanRestriction } from "../../../context/PlanRestrictionContext";
import ModalComponent from "../../../components/modal";
import { parseData } from "../../../utils";
import { showErrorAlert } from "../../../utils/alert";
import UploadProfileImg from "./uploadImg";
import { FaPlus, FaMagic } from "react-icons/fa";
import { FiUser, FiMail, FiMapPin, FiDollarSign, FiUsers, FiAlertTriangle } from "react-icons/fi";
import AddDepartment from "../../../parts/configuration/addDepartment";
import AddLocation from "../../../parts/configuration/addLocation";
import AddReportTo from "../../../parts/configuration/addReportTo";

const AddEmployee = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const inputRef = useRef();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { convertFile, filebase64 } = useImageUpload();
  const { canAddEmployees, isEmployeeLimitReached, showEmployeeLimitRestriction } = usePlanRestriction();
  const employeeData = parseData(sessionStorage.getItem("employee"));
  const todo = sessionStorage.getItem("todo");
  const [imgUpload, setImgUpload] = useState(false);
  const hasLoadedEditData = useRef(false);
  const lastEditedEmployeeId = useRef(null);
  
  // Quick add modal states
  const [quickAddType, setQuickAddType] = useState("");
  const [quickAddData, setQuickAddData] = useState("");
  const [quickAddModalOpen, setQuickAddModalOpen] = useState(false);
  
  // Department modal state
  const { isOpen: isDepartmentModalOpen, onOpen: onDepartmentModalOpen, onClose: onDepartmentModalClose } = useDisclosure();
  
  // Location modal state
  const { isOpen: isLocationModalOpen, onOpen: onLocationModalOpen, onClose: onLocationModalClose } = useDisclosure();
  
  // Reporting Senior modal state
  const { isOpen: isReportToModalOpen, onOpen: onReportToModalOpen, onClose: onReportToModalClose } = useDisclosure();
  
  const [inputData, setInputData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    employee_id: "",
    designation: "",
    salary: "",
    location_id: "",
    department_id: "",
    reporting_senior_id: "",
    shift_id: "",
    profile_image: "",
  });

  const { refetchGetEmployees } = useGetEmployees({ enabled: true });
  const { getLocationsData, getLocationsIsLoading, getLocationsIsFetched, refetchGetLocations } =
    useFetchLocations({});
  const { getDepartmentData, getDepartmentIsLoading, getDepartmentIsFetched, refetchGetDepartment } =
    useFetchDepartments({});
  const { getReportToData, getReportToIsLoading, getReportToIsFetched, refetchGetReportTo } =
    useFetchReportTo({});
  const { getShiftData, getShiftIsLoading, getShiftIsFetched, refetchGetShifts } = useFetchShift({});
  
  // Quick add hooks are no longer needed since we use configuration modals
  // The configuration components handle their own API calls

  const { addEmployeePayload, addEmployeeIsLoading } = useAddEmployees(
    (res) => {
      setImgUpload(false)
      onOpen();
    }
  );
  const { editEmployeePayload, editEmployeeIsLoading } = useEditEmployee(
    (res) => onOpen()
  );

  // Generate employee ID function - 4 digits only
  const generateEmployeeId = () => {
    // Generate a random 4-digit number
    const employeeId = Math.floor(1000 + Math.random() * 9000).toString();
    setInputData({ ...inputData, employee_id: employeeId });
    toast({
      title: "Employee ID generated!",
      description: `Generated ID: ${employeeId}`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Validate employee ID format
  const validateEmployeeId = (value) => {
    if (!value) return true; // Allow empty for required validation
    const regex = /^\d{4}$/;
    return regex.test(value);
  };

  // Handle employee ID input change with validation
  const handleEmployeeIdChange = (e) => {
    const value = e.target.value;
    // Only allow numbers and limit to 4 digits
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    setInputData({ ...inputData, employee_id: numericValue });
  };

  // Quick add functions
  const openQuickAddModal = (type) => {
    if (type === 'shift') {
      // For shifts, redirect immediately to the configuration page
      window.open('/configuration#shift', '_blank');
      toast({
        title: "Redirecting to Shift Configuration",
        description: "Please add your shift from the Configuration page, then return here to select it.",
        status: "info",
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    
    if (type === 'department') {
      // For departments, open the configuration modal
      onDepartmentModalOpen();
      return;
    }
    
    if (type === 'location') {
      // For locations, open the configuration modal
      onLocationModalOpen();
      return;
    }
    
    if (type === 'reporting_senior') {
      // For reporting seniors, open the configuration modal
      onReportToModalOpen();
      return;
    }
    
    setQuickAddType(type);
    setQuickAddModalOpen(true);
  };

  const handleQuickAdd = () => {
    if (!quickAddData.trim()) {
      toast({
        title: "Please enter a value",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // Validate input length
    if (quickAddData.trim().length < 2) {
      toast({
        title: "Input too short",
        description: "Please enter at least 2 characters",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // This function is now only used for any remaining simple quick adds
    // Most configuration types now use their own modals
    toast({
      title: "Invalid configuration type",
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  };

  const getQuickAddTitle = () => {
    switch (quickAddType) {
      case 'location': return 'Add New Location';
      case 'department': return 'Add New Department';
      case 'reporting_senior': return 'Add New Reporting Senior';
      case 'shift': return 'Add New Shift';
      default: return 'Add New Item';
    }
  };

  const getQuickAddPlaceholder = () => {
    switch (quickAddType) {
      case 'location': return 'Enter location address';
      case 'department': return 'Enter department name';
      case 'reporting_senior': return 'Enter reporting senior name';
      case 'shift': return 'Enter shift name';
      default: return 'Enter value';
    }
  };

  const getQuickAddLoading = () => {
    return false; // No longer loading for quick adds
  };

  const handleAddEmployee = () => {
    // Check employee limit before adding
    if (todo !== "edit" && !canAddEmployees()) {
      showEmployeeLimitRestriction();
      return;
    }

    // Validate employee ID format
    if (inputData?.employee_id && !validateEmployeeId(inputData?.employee_id)) {
      toast({
        title: "Invalid Employee ID",
        description: "Employee ID must be exactly 4 digits (numbers only)",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const payload = {
      ...inputData,
      // profile_image: inputData?.profile_image,
    };

    if (todo === "edit") {
      editEmployeePayload(payload);
    } else {
      addEmployeePayload(payload);
    }
  };

  const handleEdit = useCallback(async () => {
    console.log("Employee Data for Edit:", employeeData);
    if (
      !getDepartmentData ||
      !getLocationsData ||
      !getReportToData ||
      !getShiftData
    ) {
      console.log("Configuration data not loaded yet");
      return;
    }

    const {
      first_name,
      last_name,
      salary,
      designation,
      employee_id,
      phone_number,
      email,
      profile_image,
      location_id,
      department_id,
      reporting_senior_id,
      shift_id,
      location,
      department,
      reporting_senior,
      shift
    } = employeeData;

    try {
      // First try to use the actual IDs if they exist
      let finalLocationId = location_id;
      let finalDepartmentId = department_id;
      let finalReportingSeniorId = reporting_senior_id;
      let finalShiftId = shift_id;

      // If IDs don't exist, try to find by name/address
      if (!finalLocationId && location) {
        const locationMatch = getLocationsData?.find(
          (d) => d?.address === location
        );
        finalLocationId = locationMatch?.id;
      }

      if (!finalDepartmentId && department) {
        const deptMatch = getDepartmentData?.find(
          (d) => d?.name === department
        );
        finalDepartmentId = deptMatch?.id;
      }

      if (!finalReportingSeniorId && reporting_senior) {
        const reportMatch = getReportToData?.find(
          (d) => d?.name === reporting_senior
        );
        finalReportingSeniorId = reportMatch?.id;
      }

      if (!finalShiftId && shift) {
        const shiftMatch = getShiftData?.find(
          (d) => d?.name === shift
        );
        finalShiftId = shiftMatch?.id;
      }

      console.log("Mapped IDs:", {
        location_id: finalLocationId,
        department_id: finalDepartmentId,
        reporting_senior_id: finalReportingSeniorId,
        shift_id: finalShiftId
      });

      setInputData({
        first_name: first_name || "",
        last_name: last_name || "",
        email: email || "",
        phone_number: phone_number || "",
        employee_id: employee_id || "",
        designation: designation || "",
        salary: salary ? String(salary) : "",
        location_id: finalLocationId || "",
        department_id: finalDepartmentId || "",
        reporting_senior_id: finalReportingSeniorId || "",
        shift_id: finalShiftId || "",
        profile_image: profile_image || "",
      });
    } catch (error) {
      console.error("Error in handleEdit:", error);
      showErrorAlert("Something went wrong while loading employee data");
    }
  }, [getShiftData, getReportToData, getLocationsData, getDepartmentData, employeeData]);

  useEffect(() => {
    // Reset the flag when switching between edit/add modes
    if (todo !== "edit") {
      hasLoadedEditData.current = false;
      lastEditedEmployeeId.current = null;
      return;
    }
    
    // Reset if editing a different employee
    const currentEmployeeId = employeeData?.employee_id;
    if (currentEmployeeId && lastEditedEmployeeId.current !== currentEmployeeId) {
      hasLoadedEditData.current = false;
      lastEditedEmployeeId.current = currentEmployeeId;
    }
    
    if (todo === "edit" && employeeData && !hasLoadedEditData.current) {
      // Only call handleEdit once when all configuration data is loaded
      if (getLocationsIsFetched && getDepartmentIsFetched && getReportToIsFetched && getShiftIsFetched) {
        handleEdit();
        hasLoadedEditData.current = true;
      }
    }
  }, [todo, employeeData?.employee_id, getLocationsIsFetched, getDepartmentIsFetched, getReportToIsFetched, getShiftIsFetched, handleEdit]);

  // Monitor for errors in quick add operations
  // useEffect(() => {
  //   if (addLocationError) {
  //     toast({
  //       title: "Failed to add location",
  //       description: addLocationError,
  //       status: "error",
  //       duration: 5000,
  //       isClosable: true,
  //     });
  //   }
  // }, [addLocationError, toast]);

  // useEffect(() => {
  //   if (addReportToError) {
  //     toast({
  //       title: "Failed to add reporting senior",
  //       description: addReportToError,
  //       status: "error",
  //       duration: 5000,
  //       isClosable: true,
  //     });
  //   }
  // }, [addReportToError, toast]);

  return (
    <>
      <Box w="100%" bg="gray.50" minH="100vh">
        <Container maxW="4xl" py={8}>
          {/* Header */}
          <VStack spacing={6} align="stretch">
            <HStack spacing={4} align="center">
              <IconButton
                icon={<ArrowBackIcon />}
                aria-label="Go back"
                variant="ghost"
                color="gray.600"
                onClick={() => navigate(-1)}
                _hover={{ bg: "gray.100" }}
              />
              <VStack align="start" spacing={1}>
                <Heading size="lg" color="gray.900" fontWeight="600">
                  {todo === "edit" ? "Edit" : "Add New"} Employee
                </Heading>
                <Text color="gray.600" fontSize="sm">
                  {todo === "edit" ? "Update employee information" : "Fill in the details to add a new employee"}
                </Text>
              </VStack>
            </HStack>

            {/* Main Form Card */}
            <Box
              bg="white"
              p={8}
              borderRadius="20px"
              shadow="sm"
              border="1px solid"
              borderColor="gray.200"
            >
              {/* Profile Image Section */}
              <VStack spacing={6} align="center" mb={8}>
                <Box position="relative">
                  <Avatar
                    size="2xl"
                    src={inputData?.profile_image}
                    name={`${inputData?.first_name} ${inputData?.last_name}`}
                    border="3px solid"
                    borderColor="gray.200"
                  />
                  <IconButton
                    icon={<FaPlus />}
                    size="sm"
                    colorScheme="blue"
                    borderRadius="full"
                    position="absolute"
                    bottom={0}
                    right={0}
                    onClick={() => {
                      setImgUpload(true);
                      onOpen();
                    }}
                    _hover={{ transform: "scale(1.1)" }}
                    transition="all 0.2s"
                  />
                </Box>
                <VStack spacing={2}>
                  <Text fontSize="lg" fontWeight="600" color="gray.900">
                    Profile Photo
                  </Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center">
                    Click the + button to upload a profile photo
                  </Text>
                </VStack>
              </VStack>

              <input
                type="file"
                onChange={(e) => {
                  return convertFile(e.target.files);
                }}
                ref={inputRef}
                style={{ display: "none" }}
              />
              {/* Personal Information Section */}
              <VStack spacing={6} align="stretch">
                <Box>
                  <HStack spacing={2} mb={4}>
                    <FiUser color="blue.500" />
                    <Text fontSize="lg" fontWeight="600" color="gray.900">
                      Personal Information
                    </Text>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        First Name
                      </FormLabel>
                      <Input
                        placeholder="Enter first name"
                        value={inputData?.first_name}
                        onChange={(e) =>
                          setInputData({ ...inputData, first_name: e.target.value })
                        }
                        borderRadius="12px"
                        borderColor="gray.200"
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                        _hover={{ borderColor: "gray.300" }}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Last Name
                      </FormLabel>
                      <Input
                        placeholder="Enter last name"
                        value={inputData?.last_name}
                        onChange={(e) =>
                          setInputData({ ...inputData, last_name: e.target.value })
                        }
                        borderRadius="12px"
                        borderColor="gray.200"
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                        _hover={{ borderColor: "gray.300" }}
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* Contact Information Section */}
                <Box>
                  <HStack spacing={2} mb={4}>
                    <FiMail color="green.500" />
                    <Text fontSize="lg" fontWeight="600" color="gray.900">
                      Contact Information
                    </Text>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Email Address
                      </FormLabel>
                      <Input
                        placeholder="Enter email address"
                        value={inputData?.email}
                        onChange={(e) =>
                          setInputData({ ...inputData, email: e.target.value })
                        }
                        borderRadius="12px"
                        borderColor="gray.200"
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                        _hover={{ borderColor: "gray.300" }}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Phone Number
                      </FormLabel>
                      <Input
                        placeholder="Enter phone number"
                        value={inputData?.phone_number}
                        onChange={(e) =>
                          setInputData({ ...inputData, phone_number: e.target.value })
                        }
                        borderRadius="12px"
                        borderColor="gray.200"
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                        _hover={{ borderColor: "gray.300" }}
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* Employment Information Section */}
                <Box>
                  <HStack spacing={2} mb={4}>
                    <FiUsers color="purple.500" />
                    <Text fontSize="lg" fontWeight="600" color="gray.900">
                      Employment Information
                    </Text>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired isInvalid={inputData?.employee_id && !validateEmployeeId(inputData?.employee_id)}>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Employee ID
                      </FormLabel>
                      <InputGroup>
                        <Input
                          placeholder="Enter 4-digit employee ID"
                          value={inputData?.employee_id}
                          onChange={handleEmployeeIdChange}
                          readOnly={todo === "edit"}
                          bg={todo === "edit" ? "gray.50" : "white"}
                          cursor={todo === "edit" ? "not-allowed" : "text"}
                          borderRadius="12px"
                          borderColor={inputData?.employee_id && !validateEmployeeId(inputData?.employee_id) ? "red.300" : "gray.200"}
                          _focus={{ 
                            borderColor: inputData?.employee_id && !validateEmployeeId(inputData?.employee_id) ? "red.500" : "blue.500", 
                            boxShadow: inputData?.employee_id && !validateEmployeeId(inputData?.employee_id) 
                              ? "0 0 0 1px var(--chakra-colors-red-500)" 
                              : "0 0 0 1px var(--chakra-colors-blue-500)" 
                          }}
                          _hover={{ borderColor: inputData?.employee_id && !validateEmployeeId(inputData?.employee_id) ? "red.400" : "gray.300" }}
                          maxLength={4}
                        />
                        <InputRightElement width="4.5rem">
                          {todo !== "edit" && (
                            <Button
                              h="1.75rem"
                              size="sm"
                              onClick={generateEmployeeId}
                              colorScheme="blue"
                              variant="ghost"
                              leftIcon={<FaMagic />}
                              title="Generate 4-digit Employee ID"
                            >
                              Auto
                            </Button>
                          )}
                        </InputRightElement>
                      </InputGroup>
                      {inputData?.employee_id && !validateEmployeeId(inputData?.employee_id) && (
                        <Text fontSize="xs" color="red.500" mt={1}>
                          Employee ID must be exactly 4 digits (numbers only)
                        </Text>
                      )}
                      {todo === "edit" && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Employee ID cannot be changed after creation
                        </Text>
                      )}
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Designation
                      </FormLabel>
                      <Input
                        placeholder="Enter designation"
                        value={inputData?.designation}
                        onChange={(e) =>
                          setInputData({ ...inputData, designation: e.target.value })
                        }
                        borderRadius="12px"
                        borderColor="gray.200"
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                        _hover={{ borderColor: "gray.300" }}
                      />
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* Location and Department Section */}
                <Box>
                  <HStack spacing={2} mb={4}>
                    <FiMapPin color="orange.500" />
                    <Text fontSize="lg" fontWeight="600" color="gray.900">
                      Location & Department
                    </Text>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Location
                      </FormLabel>
                      <HStack spacing={2}>
                        <Box flex={1}>
                          <Select
                            placeholder="Select location"
                            value={getLocationsData?.find(item => item?.id === inputData.location_id) ? {
                              label: getLocationsData?.find(item => item?.id === inputData.location_id)?.address,
                              value: inputData.location_id
                            } : null}
                            onChange={(d) =>
                              setInputData({ ...inputData, location_id: d?.value })
                            }
                            options={getLocationsData?.map((item) => ({
                              label: item?.address,
                              value: item?.id,
                            }))}
                            isLoading={getLocationsIsLoading}
                            loadingMessage={() => "Loading locations..."}
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderRadius: "12px",
                                borderColor: "var(--chakra-colors-gray-200)",
                                minHeight: "40px",
                                "&:hover": {
                                  borderColor: "var(--chakra-colors-gray-300)",
                                },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? "var(--chakra-colors-blue-500)" : "white",
                                color: state.isSelected ? "white" : "var(--chakra-colors-gray-900)",
                                "&:hover": {
                                  backgroundColor: state.isSelected ? "var(--chakra-colors-blue-500)" : "var(--chakra-colors-gray-100)",
                                },
                              }),
                            }}
                          />
                        </Box>
                        <IconButton
                          icon={<FaPlus />}
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => openQuickAddModal('location')}
                          title="Add New Location"
                        />
                      </HStack>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Department
                      </FormLabel>
                      <HStack spacing={2}>
                        <Box flex={1}>
                          <Select
                            placeholder="Select department"
                            value={getDepartmentData?.find(item => item?.id === inputData.department_id) ? {
                              label: getDepartmentData?.find(item => item?.id === inputData.department_id)?.name,
                              value: inputData.department_id
                            } : null}
                            onChange={(d) =>
                              setInputData({ ...inputData, department_id: d?.value })
                            }
                            options={getDepartmentData?.map((item) => ({
                              label: item?.name,
                              value: item?.id,
                            }))}
                            isLoading={getDepartmentIsLoading}
                            loadingMessage={() => "Loading departments..."}
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderRadius: "12px",
                                borderColor: "var(--chakra-colors-gray-200)",
                                minHeight: "40px",
                                "&:hover": {
                                  borderColor: "var(--chakra-colors-gray-300)",
                                },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? "var(--chakra-colors-blue-500)" : "white",
                                color: state.isSelected ? "white" : "var(--chakra-colors-gray-900)",
                                "&:hover": {
                                  backgroundColor: state.isSelected ? "var(--chakra-colors-blue-500)" : "var(--chakra-colors-gray-100)",
                                },
                              }),
                            }}
                          />
                        </Box>
                        <IconButton
                          icon={<FaPlus />}
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => openQuickAddModal('department')}
                          title="Add New Department"
                        />
                      </HStack>
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* Salary and Schedule Section */}
                <Box>
                  <HStack spacing={2} mb={4}>
                    <FiDollarSign color="green.500" />
                    <Text fontSize="lg" fontWeight="600" color="gray.900">
                      Salary & Schedule
                    </Text>
                  </HStack>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Salary
                      </FormLabel>
                      <Input
                        placeholder="Enter salary amount"
                        value={inputData?.salary}
                        onChange={(e) =>
                          setInputData({ ...inputData, salary: e.target.value })
                        }
                        borderRadius="12px"
                        borderColor="gray.200"
                        _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                        _hover={{ borderColor: "gray.300" }}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                        Shift
                      </FormLabel>
                      <HStack spacing={2}>
                        <Box flex={1}>
                          <Select
                            placeholder="Select shift"
                            value={getShiftData?.find(item => item?.id === inputData.shift_id) ? {
                              label: getShiftData?.find(item => item?.id === inputData.shift_id)?.name,
                              value: inputData.shift_id
                            } : null}
                            onChange={(d) =>
                              setInputData({ ...inputData, shift_id: d?.value })
                            }
                            options={getShiftData?.map((item) => ({
                              label: item?.name,
                              value: item?.id,
                            }))}
                            isLoading={getShiftIsLoading}
                            loadingMessage={() => "Loading shifts..."}
                            styles={{
                              control: (base) => ({
                                ...base,
                                borderRadius: "12px",
                                borderColor: "var(--chakra-colors-gray-200)",
                                minHeight: "40px",
                                "&:hover": {
                                  borderColor: "var(--chakra-colors-gray-300)",
                                },
                              }),
                              option: (base, state) => ({
                                ...base,
                                backgroundColor: state.isSelected ? "var(--chakra-colors-blue-500)" : "white",
                                color: state.isSelected ? "white" : "var(--chakra-colors-gray-900)",
                                "&:hover": {
                                  backgroundColor: state.isSelected ? "var(--chakra-colors-blue-500)" : "var(--chakra-colors-gray-100)",
                                },
                              }),
                            }}
                          />
                        </Box>
                        <IconButton
                          icon={<FaPlus />}
                          colorScheme="blue"
                          variant="outline"
                          onClick={() => openQuickAddModal('shift')}
                          title="Add New Shift"
                        />
                      </HStack>
                    </FormControl>
                  </SimpleGrid>
                </Box>

                {/* Reporting Structure */}
                <Box>
                  <HStack spacing={2} mb={4}>
                    <FiUsers color="teal.500" />
                    <Text fontSize="lg" fontWeight="600" color="gray.900">
                      Reporting Structure
                    </Text>
                  </HStack>
                  <FormControl isRequired>
                    <FormLabel fontSize="sm" fontWeight="500" color="gray.700">
                      Reporting Senior
                    </FormLabel>
                    <HStack spacing={2}>
                      <Box flex={1}>
                        <Select
                          placeholder="Select reporting senior"
                          value={getReportToData?.find(item => item?.id === inputData.reporting_senior_id) ? {
                            label: getReportToData?.find(item => item?.id === inputData.reporting_senior_id)?.name,
                            value: inputData.reporting_senior_id
                          } : null}
                          onChange={(d) =>
                            setInputData({
                              ...inputData,
                              reporting_senior_id: d?.value,
                            })
                          }
                          options={getReportToData?.map((item) => ({
                            label: item?.name,
                            value: item?.id,
                          }))}
                          isLoading={getReportToIsLoading}
                          loadingMessage={() => "Loading reporting seniors..."}
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderRadius: "12px",
                              borderColor: "var(--chakra-colors-gray-200)",
                              minHeight: "40px",
                              "&:hover": {
                                borderColor: "var(--chakra-colors-gray-300)",
                              },
                            }),
                            option: (base, state) => ({
                              ...base,
                              backgroundColor: state.isSelected ? "var(--chakra-colors-blue-500)" : "white",
                              color: state.isSelected ? "white" : "var(--chakra-colors-gray-900)",
                              "&:hover": {
                                backgroundColor: state.isSelected ? "var(--chakra-colors-blue-500)" : "var(--chakra-colors-gray-100)",
                              },
                            }),
                          }}
                        />
                      </Box>
                      <IconButton
                        icon={<FaPlus />}
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => openQuickAddModal('reporting_senior')}
                        title="Add New Reporting Senior"
                      />
                    </HStack>
                  </FormControl>
                </Box>
              </VStack>

              {/* Employee Limit Warning */}
              {todo !== "edit" && isEmployeeLimitReached() && (
                <Box 
                  p={4} 
                  bg="red.50" 
                  borderRadius="md" 
                  border="1px solid" 
                  borderColor="red.200"
                  mb={4}
                >
                  <HStack spacing={3}>
                    <FiAlertTriangle color="red" />
                    <VStack align="start" spacing={1}>
                      <Text fontSize="sm" fontWeight="medium" color="red.800">
                        Employee Limit Reached
                      </Text>
                      <Text fontSize="xs" color="red.700">
                        You have reached the maximum number of employees allowed in your current plan. 
                        Upgrade to add more employees.
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              )}

              {/* Submit Button */}
              <Box w="100%" textAlign="center" mt={8}>
                <Button
                  colorScheme="blue"
                  size="lg"
                  borderRadius="12px"
                  fontWeight="600"
                  px={8}
                  py={4}
                  onClick={handleAddEmployee}
                  isLoading={addEmployeeIsLoading || editEmployeeIsLoading}
                  loadingText={todo === "edit" ? "Updating..." : "Adding..."}
                  _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
                  transition="all 0.2s"
                  isDisabled={todo !== "edit" && !canAddEmployees()}
                >
                  {todo === "edit" ? "Update Employee" : "Add Employee"}
                </Button>
              </Box>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Quick Add Modal */}
      <Modal isOpen={quickAddModalOpen} onClose={() => setQuickAddModalOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{getQuickAddTitle()}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Name</FormLabel>
              <Input
                placeholder={getQuickAddPlaceholder()}
                value={quickAddData}
                onChange={(e) => setQuickAddData(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !getQuickAddLoading()) {
                    handleQuickAdd();
                  }
                }}
                isDisabled={getQuickAddLoading()}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={() => setQuickAddModalOpen(false)}
              isDisabled={getQuickAddLoading()}
            >
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleQuickAdd}
              isLoading={getQuickAddLoading()}
              loadingText="Adding..."
              isDisabled={!quickAddData.trim() || getQuickAddLoading()}
            >
              Add
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ModalComponent
        isOpen={isOpen}
        onClose={() => false}
        size={"md"}
        title={``}
        closeBtn={imgUpload ? true : false}
        closeModal={onClose}
        bg={imgUpload ? "#fff" : "transparent"}
      >
        {!imgUpload ? (
          <EmployeeSuccess
            isOpen={isOpen}
            onClose={() => {
              navigate("/employee");
              refetchGetEmployees();
              onClose();
            }}
          />
        ) : (
          <UploadProfileImg
            {...{
              convertFile,
              inputData,
              inputRef,
              filebase64,
              setInputData,
              onClose,
              setImgUpload
            }}
          />
        )}
      </ModalComponent>

      {/* Department Configuration Modal */}
      <Modal isOpen={isDepartmentModalOpen} onClose={onDepartmentModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={0}>
            <AddDepartment
              closeModal={onDepartmentModalClose}
              editDept={null}
              todo="new"
              refetchGetDepartment={refetchGetDepartment}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Location Configuration Modal */}
      <Modal isOpen={isLocationModalOpen} onClose={onLocationModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={0}>
            <AddLocation
              closeModal={onLocationModalClose}
              editLocation={null}
              todo="new"
              refetchGetLocations={refetchGetLocations}
            />
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Reporting Senior Configuration Modal */}
      <Modal isOpen={isReportToModalOpen} onClose={onReportToModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalBody p={0}>
            <AddReportTo
              closeModal={onReportToModalClose}
              editReportTo={null}
              todo="new"
              refetchGetReportTo={refetchGetReportTo}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddEmployee;
